import React, { useEffect, useState, useRef, useCallback, useMemo } from "react";
import QrReader from "../components/QrReader";
import {
	storageKeyForDate,
	parseStoredTimbrature,
	TIMBRATURE_STORAGE_PREFIX,
	saveTimbrataOffline,
	saveRequestOffline,
	syncTimbrate as syncTimbrateQueue,
	countPendingTimbrate,
	deleteTimbrateByDate,
	isOnline,
	getOrCreateDeviceId,
	resetRetryCount,
	resetSingleRetry,
	logWssError,
} from "../utils";
import { hasPrivilege } from "../utils/permissions";
import {
	getActiveGiornataDate,
	getTimbratureByDate,
	importLegacyLocalDay,
} from "../utils/timbratureDb";
import {
	fetchAndCacheLocationContext,
	getCachedLocationContext,
} from "../utils/locationContext";
import { evaluateGpsValidation, getCurrentGpsPosition, validateGpsExecutionContext } from "../utils/geolocation";
import { addRejectedTimbrata } from "../utils/timbrateScartateDb";
import { api, ApiError } from "../api/client";
import { useMessage } from "../components/MessageProvider";

const BUTTON_THEMES = {
	green: {
		active: "btn-color-green-active",
		disabled: "btn-color-green-disabled",
	},
	amber: {
		active: "btn-color-amber-active",
		disabled: "btn-color-amber-disabled",
	},
	amberLight: {
		active: "btn-color-amber-light-active",
		disabled: "btn-color-amber-light-disabled",
	},
	red: {
		active: "btn-color-red-active",
		disabled: "btn-color-red-disabled",
	},
};

const TIMBRATURE_LABELS = [
	{
		tipo: "INIZIO_GIORNATA",
		label: "Inizio Giornata",
		icon: "\u21E2",
		layout: "col-span-2",
		theme: "green",
	},
	{
		tipo: "INIZIO_PAUSA",
		label: "Inizio Pausa",
		icon: "\u2615",
		theme: "amber",
	},
	{
		tipo: "FINE_PAUSA",
		label: "Fine Pausa",
		icon: "\u2692",
		theme: "amberLight",
	},
	{
		tipo: "FINE_GIORNATA",
		label: "Fine Giornata",
		icon: "\u21E0",
		layout: "col-span-2",
		theme: "red",
	},
];

const TIMEFRAME_ID = {
	INIZIO_GIORNATA: 1,
	INIZIO_PAUSA: 4,
	FINE_PAUSA: 5,
	FINE_GIORNATA: 8,
};

const HOURS_24 = Array.from({ length: 24 }, (_, i) => String(i).padStart(2, "0"));
const MINUTES_60 = Array.from({ length: 60 }, (_, i) => String(i).padStart(2, "0"));
const todayIso = () => new Date().toISOString().slice(0, 10);
const currentHHMM = () => {
	const now = new Date();
	return `${String(now.getHours()).padStart(2, "0")}:${String(now.getMinutes()).padStart(2, "0")}`;
};
const formatHHMM = (isoString) => {
	try {
		return new Date(isoString).toLocaleTimeString("it-IT", { hour: "2-digit", minute: "2-digit" });
	} catch (err) {
		return "--:--";
	}
};

const formatGpsCoordinate = (value) => {
	return Number.isFinite(Number(value)) ? Number(value).toFixed(6) : "n/d";
};

const buildGpsDebugMessage = ({ context, position, evaluation, accuracyLabel, distanceLabel, toleranceLabel, accuracyMaxLabel }) => {
	return [
		`Coordinate attese: ${formatGpsCoordinate(context?.latitudine)}, ${formatGpsCoordinate(context?.longitudine)}`,
		`Coordinate rilevate: ${formatGpsCoordinate(position?.latitudine)}, ${formatGpsCoordinate(position?.longitudine)}`,
		`Accuracy rilevata: ${accuracyLabel}`,
		`Distanza calcolata: ${distanceLabel}`,
		`Tolleranza configurata: ${toleranceLabel}`,
		`Soglia accuracy istanza: ${accuracyMaxLabel}`,
		`Esito calcolo: ${evaluation?.result || "n/d"}`,
		`Motivo: ${evaluation?.reason || "n/d"}`,
	].join("\n");
};

function Timbratore({ user, onSetTopBarAction }) {
		// Funzione di reset test (svuota timbrature giornata corrente)
		async function resetTest() {
			const current = todayIso();
			localStorage.removeItem(storageKeyForDate(current));
			await deleteTimbrateByDate(current, user?.id);
			await loadTimbratureFromDb();
			await refreshPendingCount();
		}
	const [orario, setOrario] = useState(new Date());
	const [timbrature, setTimbrature] = useState([]);
	const [dataRiferimento, setDataRiferimento] = useState(null);
	const [syncing, setSyncing] = useState(false);
	const [pendingCount, setPendingCount] = useState(0);
	const [lastSyncAt, setLastSyncAt] = useState(null);
	const [qrMode, setQrMode] = useState(null); // tipo timbratura in attesa QR
	const [qrError, setQrError] = useState("");
	const [qrInfo, setQrInfo] = useState("");
	const [closingScanner, setClosingScanner] = useState(false);
	const [loadingGiornata, setLoadingGiornata] = useState(true);
	const [fuoriOrarioEnabled, setFuoriOrarioEnabled] = useState(false);
	const [showFuoriOrarioModal, setShowFuoriOrarioModal] = useState(false);
	const [fuoriOrarioTime, setFuoriOrarioTime] = useState(currentHHMM);
	const [locationContext, setLocationContext] = useState(null);
	const [locationContextError, setLocationContextError] = useState(null);
	const pendingFuoriOrario = useRef(null);
	const locationContextErrorRef = useRef(null);
	const timerRef = useRef();
	const [isWindowsPlatform, setIsWindowsPlatform] = useState(false);
	const [gpsLoading, setGpsLoading] = useState(false);
	const { showMessage, errorConfigMessage } = useMessage();

	useEffect(() => {
		if (!onSetTopBarAction) return;
		onSetTopBarAction({
			icon: (
				<svg xmlns="http://www.w3.org/2000/svg" className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.8}>
					<path strokeLinecap="round" strokeLinejoin="round" d="M13 16h-1v-4h-1m1-4h.01M12 2a10 10 0 100 20A10 10 0 0012 2z" />
				</svg>
			),
			label: "Info Timbratore",
			onClick: () => showMessage({
				title: "Timbratore",
				message: "Registra le timbrature di entrata e uscita. Puoi timbrare anche offline: le timbrature vengono salvate localmente e sincronizzate non appena torni online. In caso di anomalie GPS o fuori orario, ti verrà chiesta conferma prima di procedere.",
				type: "info",
				timeout: 0,
			}),
		});
		return () => onSetTopBarAction(null);
	}, [onSetTopBarAction, showMessage]);

	const canAutoRichiedereModifica = useMemo(() => hasPrivilege(user?.permessi, "EJRI"), [user?.permessi]);
	const isDeveloper = useMemo(
		() => hasPrivilege(user?.permessi, "EJ-Developper") || hasPrivilege(user?.permessi, "EJDEV"),
		[user?.permessi]
	);
	const appendQrInfo = useCallback((message) => {
		if (!message) return;
		setQrInfo(prev => (prev ? `${prev} • ${message}` : message));
	}, []);


	useEffect(() => {
		if (typeof navigator !== "undefined" && navigator.userAgent) {
			setIsWindowsPlatform(navigator.userAgent.toLowerCase().includes("windows"));
		}
	}, []);

	// Aggiorna orologio ogni secondo
	useEffect(() => {
		timerRef.current = setInterval(() => setOrario(new Date()), 1000);
		return () => clearInterval(timerRef.current);
	}, []);

	const refreshPendingCount = useCallback(async () => {
		if (!user?.id) {
			setPendingCount(0);
			return;
		}
		const count = await countPendingTimbrate(user.id);
		setPendingCount(count);
	}, [user?.id]);

	useEffect(() => {
		refreshPendingCount();
	}, [refreshPendingCount]);

	const migrateLegacyTimbrature = useCallback(async () => {
		if (!user?.id) return;
		const prefix = `${TIMBRATURE_STORAGE_PREFIX}_`;
		const keys = [];
		for (let i = 0; i < localStorage.length; i += 1) {
			const key = localStorage.key(i);
			if (key && key.startsWith(prefix)) {
				keys.push(key);
			}
		}
		for (const key of keys) {
			const day = key.slice(prefix.length);
			const stored = parseStoredTimbrature(localStorage.getItem(key));
			localStorage.removeItem(key);
			if (!stored.length) continue;
			try {
				await importLegacyLocalDay(user.id, day, stored);
			} catch (err) {
				console.error("Errore durante la migrazione delle timbrature locali", err);
			}
		}
	}, [user?.id]);

	const loadTimbratureFromDb = useCallback(
		async (focusDate) => {
			if (!user?.id) {
				setTimbrature([]);
				setDataRiferimento(null);
				setLoadingGiornata(false);
				return;
			}
			setLoadingGiornata(true);
			await migrateLegacyTimbrature();
			let reference = focusDate;
			try {
				if (!reference) {
					reference = await getActiveGiornataDate(user.id);
				}
				if (!reference) {
					setTimbrature([]);
					setDataRiferimento(null);
					return;
				}
				const records = await getTimbratureByDate(user.id, reference);
				if (!records.length) {
					setTimbrature([]);
					setDataRiferimento(null);
				} else {
					setTimbrature(records);
					setDataRiferimento(reference);
				}
			} catch (err) {
				console.error("Errore caricamento timbrature IndexedDB", err);
				setQrError("Impossibile leggere le timbrature salvate sul dispositivo.");
			} finally {
				setLoadingGiornata(false);
				setLastSyncAt(new Date());
			}
		},
		[migrateLegacyTimbrature, user?.id]
	);

	const finalizeTimbratura = useCallback(
		async ({ tipo, qrText, qrDecoded, overrideTime, gpsValidation }) => {
			if (!user?.id) return false;
			setQrMode(null);
			let nuovaDataRif = dataRiferimento || todayIso();
			if (tipo === "INIZIO_GIORNATA") {
				nuovaDataRif = todayIso();
				setDataRiferimento(nuovaDataRif);
			}
			const isFineGiornata = tipo === "FINE_GIORNATA";
			const oraReale = new Date();
			const infoTimeLabel = overrideTime || oraReale.toLocaleTimeString("it-IT");
			const nuova = {
				tipo,
				orario: oraReale.toISOString(),
				id: Date.now(),
				data_riferimento: nuovaDataRif,
				timeframe_id: TIMEFRAME_ID[tipo] ?? null,
				data_sync: null,
				qr: qrText,
				qr_decoded: qrDecoded,
				device_id: qrDecoded?.dev || null,
				fuori_orario: Boolean(overrideTime),
				causal: overrideTime ? `FUORI_ORARIO ${overrideTime}` : null,
				orario_teorico: overrideTime || null,
			};
			if (gpsValidation) {
				nuova.device_id = getOrCreateDeviceId();
				nuova.gps_distance = gpsValidation.distance;
				nuova.gps_accuracy = gpsValidation.accuracy;
				nuova.location_id = gpsValidation.locationId || null;
				nuova.mode_localizzazione = "GPS";
				nuova.lat = gpsValidation.lat ?? null;
				nuova.lng = gpsValidation.lng ?? null;
			}
			if (!qrDecoded?.dev && !gpsValidation) {
				delete nuova.device_id;
			}
			if (isFineGiornata) {
				nuova.fineGiornata = true;
			}
			try {
				await saveTimbrataOffline({ ...nuova, user_id: user.id, assunzione_id: user.assunzione_id });
				await loadTimbratureFromDb(isFineGiornata ? null : nuovaDataRif);
				await refreshPendingCount();
			} catch (err) {
				console.error("Errore salvataggio timbrata offline", err);
				if (gpsValidation) {
					await showMessage({
						title: "Verifica location",
						message: "Impossibile registrare la timbrata. Riprova.",
						type: "error",
					});
				} else {
					setQrError("Errore durante il salvataggio locale della timbratura.");
				}
				pendingFuoriOrario.current = null;
				return false;
			}
			if (navigator.onLine) {
				await performSync({ silent: true, focusDate: nuovaDataRif });
			}
			if (isFineGiornata) {
				setDataRiferimento(null);
			}
			setFuoriOrarioEnabled(false);
			if (isDeveloper) {
				if (gpsValidation) {
					setQrInfo("");
				} else {
					setQrInfo(`Location ${qrDecoded.dev} alle ${infoTimeLabel}${overrideTime ? " (fuori orario)" : ""}`);
				}
			} else {
				setQrInfo("");
			}
			pendingFuoriOrario.current = null;
			return true;
		},
		[dataRiferimento, isDeveloper, loadTimbratureFromDb, refreshPendingCount, showMessage, user?.assunzione_id, user?.id, performSync]
	);

	const refreshLocationContext = useCallback(
		async ({ forceRemote = false } = {}) => {
			if (!user?.id || !user?.assunzione_id) {
				const nextError = {
					code: "GPS_CONFIG_INVALID_USER_CONTEXT",
					message: "Dati utente insufficienti per recuperare la location pianificata.",
					details: {
						user_id: user?.id || null,
						assunzione_id: user?.assunzione_id || null,
					},
				};
				locationContextErrorRef.current = nextError;
				setLocationContextError(nextError);
				return null;
			}
			const cached = await getCachedLocationContext(user);
			if (cached && !forceRemote) {
				setLocationContext(cached);
				locationContextErrorRef.current = null;
				setLocationContextError(null);
			}
			if (!isOnline()) {
				if (!cached) {
					const nextError = {
						code: "GPS_CONTEXT_OFFLINE_UNAVAILABLE",
						message: "Contesto location non disponibile offline. Contatta l'amministrazione.",
						details: { offline: true },
					};
					locationContextErrorRef.current = nextError;
					setLocationContextError(nextError);
				}
				return cached;
			}
			try {
				const fresh = await fetchAndCacheLocationContext(user, {
					dataOra: new Date().toISOString().slice(0, 16).replace("T", " "),
				});
				setLocationContext(fresh);
				locationContextErrorRef.current = null;
				setLocationContextError(null);
				return fresh;
			} catch (err) {
				console.error("Errore recupero location pianificata", err);
				if (cached) {
					setLocationContext(cached);
					locationContextErrorRef.current = null;
					setLocationContextError(null);
					return cached;
				}
				const nextError = {
					code: err?.code || "GPS_CONTEXT_FETCH_ERROR",
					message: err?.message || "Impossibile recuperare la location pianificata.",
					details: err?.details || {
						offline: !isOnline(),
						user_id: user?.id || null,
						assunzione_id: user?.assunzione_id || null,
						has_token: Boolean(user?.token),
					},
				};
				locationContextErrorRef.current = nextError;
				setLocationContextError(nextError);
				return null;
			}
		},
		[user]
	);

	const gestisciFuoriOrarioConseguenze = useCallback(
		async ({ actualHHMM, overrideHHMM, referenceDate }) => {
			if (!overrideHHMM) return;
			if (!canAutoRichiedereModifica || !user?.id || !user?.cliente_id || !user?.assunzione_id) {
				appendQrInfo("Timbrata registrata. Comunica la modifica all'ufficio del personale.");
				return;
			}
			const effectiveDate = referenceDate || todayIso();
			const payload = {
				session_token: user.token,
				richiesta_tipologia_id: 23,
				data_dal: effectiveDate,
				data_al: effectiveDate,
				valore: 0,
				note: `Dalle ${actualHHMM} alle ${overrideHHMM}`,
				user_richiesta_id: user.id,
				cliente_id: user.cliente_id,
				assunzione_id: user.assunzione_id,
			};
			try {
				if (!isOnline()) {
					await saveRequestOffline(payload);
					appendQrInfo("Richiesta di modifica salvata offline: verrà inviata appena torna la connessione.");
					return;
				}
				const data = await api.post("/api/richieste", payload);
				if (data?.success) {
					appendQrInfo("Richiesta di modifica inviata all'HR.");
				} else {
					await logWssError({ endpoint: "createRichiestaGiustificativi", payload, response: data, status: 200 });
					setQrError("Impossibile inviare la richiesta di modifica. Contatta l'HR.");
				}
			} catch (err) {
				const status = err instanceof ApiError ? err.status : 0;
				const response = err instanceof ApiError ? err.body : String(err);
				await logWssError({ endpoint: "createRichiestaGiustificativi", payload, response, status });
				setQrError("Errore durante la creazione della richiesta di modifica.");
			}
		},
		[appendQrInfo, canAutoRichiedereModifica, user]
	);

	const handleFuoriOrarioConfirm = async () => {
		const payload = pendingFuoriOrario.current;
		if (!payload) {
			setShowFuoriOrarioModal(false);
			return;
		}
		const actualHHMM = new Date().toLocaleTimeString("it-IT", { hour: "2-digit", minute: "2-digit" });
		const referenceDate = dataRiferimento || todayIso();
		setShowFuoriOrarioModal(false);
		await finalizeTimbratura({ ...payload, overrideTime: fuoriOrarioTime });
		await gestisciFuoriOrarioConseguenze({ actualHHMM, overrideHHMM: fuoriOrarioTime, referenceDate });
	};

	const handleFuoriOrarioCancel = async () => {
		const payload = pendingFuoriOrario.current;
		setShowFuoriOrarioModal(false);
		if (!payload) {
			setQrInfo("Timbrata annullata");
			return;
		}
		const proceed = window.confirm("Vuoi inviare la timbrata regolarmente con l'orario attuale?");
		if (proceed) {
			await finalizeTimbratura(payload);
		} else {
			pendingFuoriOrario.current = null;
			setQrInfo("Timbrata annullata");
		}
	};

	// Carica la giornata in corso da IndexedDB (eventualmente migrando i dati legacy)
	useEffect(() => {
		loadTimbratureFromDb();
	}, [loadTimbratureFromDb]);

	useEffect(() => {
		refreshLocationContext();
	}, [refreshLocationContext]);

	const resolveLocationStrategy = useCallback((context) => {
		if (!context) {
			return { valid: false, reason: "missing" };
		}
		if (!context.localizzazione) {
			return { valid: false, reason: "disabled" };
		}
		const mode = typeof context.mode_localizzazione === "string"
			? context.mode_localizzazione.trim().toUpperCase()
			: "";
		if (!mode) {
			return { valid: false, reason: "missing-mode" };
		}
		return { valid: true, mode };
	}, []);

	const buildLocationStrategyConfigError = useCallback((strategy) => {
		switch (strategy?.reason) {
			case "missing":
				return {
					code: "GPS_CONFIG_MISSING_CONTEXT",
					message: "Contesto location non disponibile. Contatta l'amministrazione.",
				};
			case "disabled":
				return {
					code: "GPS_CONFIG_DISABLED",
					message: "La localizzazione non e' abilitata per questa timbrata. Contatta l'amministrazione.",
				};
			case "missing-mode":
				return {
					code: "GPS_CONFIG_MISSING_MODE",
					message: "Modalita' di localizzazione non configurata. Contatta l'amministrazione.",
				};
			default:
				return {
					code: "GPS_CONFIG_INVALID_STATE",
					message: "Verifica impostazioni location. Contatta l'amministrazione.",
				};
		}
	}, []);

	const handleGpsTimbratura = useCallback(
		async (tipo, context) => {
			const configValidation = validateGpsExecutionContext(context);

			if (!configValidation.ok) {
				await errorConfigMessage({
					title: "Verifica location",
					message: configValidation.message,
					code: configValidation.code,
					details: configValidation.details,
				});
				return;
			}

			setGpsLoading(true);
			try {
				const position = await getCurrentGpsPosition();
				const evaluation = evaluateGpsValidation({
					currentPosition: position,
					expectedPosition: context,
					tolleranza: context.tolleranza,
					accuracy: position?.accuracy,
					maxUnusableAccuracy: context.gps_accuracy_max,
				});
				const accuracyLabel = evaluation.accuracy != null ? `${Math.round(evaluation.accuracy)} m` : "n/d";
				const distanceLabel = Number.isFinite(evaluation.distance) ? `${evaluation.distance.toFixed(1)} m` : "n/d";
				const toleranceLabel = Number.isFinite(evaluation.tolerance) ? `${evaluation.tolerance} m` : "n/d";
				const accuracyMaxLabel = Number.isFinite(evaluation.maxUnusableAccuracy) ? `${evaluation.maxUnusableAccuracy} m` : "n/d";
				setGpsLoading(false);
				if (isDeveloper) {
					await showMessage({
						title: "Debug GPS",
						message: buildGpsDebugMessage({
							context,
							position,
							evaluation,
							accuracyLabel,
							distanceLabel,
							toleranceLabel,
							accuracyMaxLabel,
						}),
						type: evaluation.result === "VALIDA" ? "success" : "warning",
						timeout: 0,
					});
				}

				if (evaluation.result !== "VALIDA" && !user?.gps_check_disabilitato) {
					await addRejectedTimbrata({
						user_id: user?.id,
						assunzione_id: user?.assunzione_id,
						tipo,
						data_riferimento: dataRiferimento || todayIso(),
						location_id: context.location_id || context.gruppo_id || null,
						mode_localizzazione: "GPS",
						gps_distance: evaluation.distance,
						gps_accuracy: evaluation.accuracy,
						gps_tolleranza: evaluation.tolerance,
						gps_result: evaluation.result,
						gps_reason: evaluation.reason,
					});

					await showMessage({
						title: "Verifica location",
						message: evaluation.result === "FUORI_AREA"
							? "Distanza eccessiva dalla location prevista."
							: "Connessione scarsa. Attendi qualche secondo e riprova.",
						type: "warning",
						timeout: 0,
					});
					return;
				}

				if (fuoriOrarioEnabled) {
					pendingFuoriOrario.current = {
						tipo,
						gpsValidation: {
							distance: evaluation.distance,
							accuracy: evaluation.accuracy,
							locationId: context.location_id || context.gruppo_id || null,
							lat: position?.latitudine ?? null,
							lng: position?.longitudine ?? null,
						},
					};
					setFuoriOrarioTime(currentHHMM());
					setShowFuoriOrarioModal(true);
					return;
				}
				const finalized = await finalizeTimbratura({
					tipo,
					gpsValidation: {
						distance: evaluation.distance,
						accuracy: evaluation.accuracy,
						locationId: context.location_id || context.gruppo_id || null,
						lat: position?.latitudine ?? null,
						lng: position?.longitudine ?? null,
					},
				});
				if (!finalized) {
					return;
				}
				await showMessage({
					title: "Verifica location",
					message: "Timbrata registrata correttamente.",
					type: "success",
					timeout: 2,
				});
			} catch (err) {
				setGpsLoading(false);
				await showMessage({
					title: "Verifica location",
					message: err?.message || "Impossibile rilevare la posizione GPS. Riprova.",
					type: "warning",
				});
			} finally {
				setGpsLoading(false);
			}
		},
		[dataRiferimento, errorConfigMessage, finalizeTimbratura, fuoriOrarioEnabled, showMessage, user?.assunzione_id, user?.id, user?.gps_check_disabilitato]
	);


	// Logica abilitazione pulsanti
	function isButtonEnabled(tipo) {
		if (!dataRiferimento) return tipo === "INIZIO_GIORNATA";
		const giornata = timbrature.filter(t => t.data_riferimento === dataRiferimento);
		if (giornata.length === 0) return tipo === "INIZIO_GIORNATA";
		const last = giornata[giornata.length - 1].tipo;
		if (tipo === "INIZIO_GIORNATA") return false;
		if (tipo === "INIZIO_PAUSA") return last === "INIZIO_GIORNATA" || last === "FINE_PAUSA";
		if (tipo === "FINE_PAUSA") return last === "INIZIO_PAUSA";
		if (tipo === "FINE_GIORNATA") return last === "FINE_PAUSA" || last === "INIZIO_GIORNATA";
		return false;
	}

	// Aggiungi timbratura
	async function handleTimbratura(tipo) {
		setQrError("");
		setQrInfo("");
		setClosingScanner(false);
		const refreshedContext = await refreshLocationContext({ forceRemote: true });
		const currentContext = refreshedContext || locationContext;
		const strategy = resolveLocationStrategy(currentContext);

		if (!strategy.valid) {
			const configError = strategy.reason === "missing" && locationContextErrorRef.current
				? locationContextErrorRef.current
				: buildLocationStrategyConfigError(strategy);
			await errorConfigMessage({
				title: "Verifica location",
				message: configError.message,
				code: configError.code,
				details: configError.details,
			});
			return;
		}

		if (strategy.mode === "GPS") {
			await handleGpsTimbratura(tipo, currentContext);
			return;
		}

		if (strategy.mode !== "QR") {
			await errorConfigMessage({
				title: "Verifica location",
				message: "La modalita' di localizzazione configurata non e' supportata. Contatta l'amministrazione.",
				code: "GPS_CONFIG_UNSUPPORTED_MODE",
			});
			return;
		}

		if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
			setQrError("Nessuna fotocamera disponibile sul dispositivo. Impossibile timbrare.");
			return;
		}
		setQrMode(tipo);
	}

	async function performSync({ silent = false, focusDate } = {}) {
		if (!navigator.onLine) {
			if (!silent) setQrError("Connessione assente. Riprova quando la rete è disponibile.");
			return { ok: false, error: "offline" };
		}
		if (!user?.id) {
			if (!silent) setQrError("Utente non valido. Effettua nuovamente l'accesso.");
			return { ok: false, error: "invalid-user" };
		}
		if (!silent) {
			setSyncing(true);
			setQrError("");
			setQrInfo("");
		}
		const result = await syncTimbrateQueue(user);
		if (!silent) {
			setSyncing(false);
		}
		await refreshPendingCount();
		const referenceDate = focusDate || dataRiferimento || todayIso();
		await loadTimbratureFromDb(referenceDate);
		if (!result.ok) {
			return result;
		}
		setLastSyncAt(new Date());
		if (referenceDate && result.clearedDates?.includes(referenceDate)) {
			setDataRiferimento(null);
			setTimbrature([]);
		}
		if (!silent) {
			if (result.synced > 0) {
				setQrInfo("Timbrature sincronizzate correttamente.");
			} else {
				setQrInfo("Nessuna nuova timbratura da sincronizzare.");
			}
		}
		return result;
	}

	// Callback quando il QR viene letto
	async function onQrScan(qrText) {
		if (!qrText || typeof qrText !== "string") {
			setQrError("QR non riconosciuto. Riprova.");
			return;
		}
		const tipoSelezionato = qrMode;
		setQrError("");
		setQrInfo("");
		if (!tipoSelezionato) {
			setQrError("Nessuna timbratura selezionata.");
			return;
		}
		if (!user?.id) {
			setQrError("Utente non valido. Effettua nuovamente l'accesso.");
			return;
		}
		// Step 1: Decodifica Base64 e parse JSON
		let obj;
		try {
			const raw = atob(qrText);
			obj = JSON.parse(raw);
		} catch (e) {
			setQrError("QR non riconosciuto. Riprova.");
			return;
		}
		// Step 2: Verifica firma
		function verifySignature(obj) {
			const dt = obj.dt;
			const yyyy = parseInt(dt.slice(0, 4));
			const mo = parseInt(dt.slice(4, 6));
			const dd = parseInt(dt.slice(6, 8));
			const hh = parseInt(dt.slice(8, 10));
			const mm = parseInt(dt.slice(10, 12));
			const ss = parseInt(dt.slice(12, 14));
			const dev = obj.dev;
			const sumDev = [...dev].reduce((a, c) => a + c.charCodeAt(0), 0);
			const expectedSig = (hh * 97 + mm * 59 + ss * 31 + yyyy + mo + dd + sumDev) % 100000;
			return expectedSig === obj.sig;
		}
		if (!verifySignature(obj)) {
			setQrError("QR code non abilitato. Riprovare.");
			return;
		}
		// Step 3: Controllo timestamp (entro 120s)
		function parseTimestamp(dt) {
			const yyyy = parseInt(dt.slice(0, 4));
			const mm = parseInt(dt.slice(4, 6)) - 1;
			const dd = parseInt(dt.slice(6, 8));
			const hh = parseInt(dt.slice(8, 10));
			const mi = parseInt(dt.slice(10, 12));
			const ss = parseInt(dt.slice(12, 14));
			return new Date(yyyy, mm, dd, hh, mi, ss);
		}
		const qrDate = parseTimestamp(obj.dt);
		const now = new Date();
		const diffSec = Math.abs((now.getTime() - qrDate.getTime()) / 1000);
		if (diffSec > 120) {
			setQrError("QR scaduto. Riprova.");
			return;
		}
		// Step 4: Verifica che il QR abbia il codice dispositivo
		if (!obj.dev || typeof obj.dev !== "string" || obj.dev.length < 3) {
			setQrError("QR code non abilitato. Riprovare.");
			return;
		}
		// Se tutto ok, registra la timbratura (con eventuale orario manuale)
		if (fuoriOrarioEnabled) {
			pendingFuoriOrario.current = { tipo: tipoSelezionato, qrText, qrDecoded: obj };
			setFuoriOrarioTime(currentHHMM());
			setShowFuoriOrarioModal(true);
			setQrMode(null);
			return;
		}
		await finalizeTimbratura({ tipo: tipoSelezionato, qrText, qrDecoded: obj });
	}

	function onQrError(err) {
		setQrError("Impossibile accedere alla fotocamera o permesso negato. La timbratura richiede la scansione del QR code della location.");
		setClosingScanner(true);
	}

	function handleScannerClosed() {
		setClosingScanner(false);
		setQrMode(null);
	}

	function handleManualClose() {
		setClosingScanner(true);
		setQrMode(null);
	}

	// Mostra badge se ci sono timbrature non sincronizzate
	const giornata = useMemo(() => (dataRiferimento ? timbrature : []), [dataRiferimento, timbrature]);
	const nonSynced = useMemo(() => pendingCount > 0 || giornata.some(t => !t.data_sync), [pendingCount, giornata]);
	const dataRifLabel = dataRiferimento
		? `Giornata in corso: ${dataRiferimento.split("-").reverse().join("/")}`
		: "Nessuna giornata in corso";

	// Icone stato sync
	function SyncIcon({ data_sync, onRetry }) {
		if (data_sync)
			return (
				<span title="Sincronizzata" className="ml-2 text-lg" style={{ color: "#059669" }}>
					&#10003;
				</span>
			);
		return (
			<button
				type="button"
				title="Riprova invio"
				onClick={onRetry}
				className="ml-2 flex items-center gap-1 text-amber-600 text-base"
			>
				<span className="text-lg">&#8635;</span>
				<span className="text-base">&#x23F3;</span>
			</button>
		);
	}

	const SyncIconButton = ({ pending, syncing, onClick }) => {
		const label = syncing ? "Sincronizzazione in corso" : pending ? "Timbrature da sincronizzare" : "Tutto sincronizzato";
		const color = syncing || pending ? "#b45309" : "#059669";
		return (
			<button
				type="button"
				title={label}
				aria-label={label}
				onClick={onClick}
				disabled={syncing}
				className={`w-12 h-12 flex items-center justify-center gap-2 rounded-full border border-transparent text-2xl ${syncing ? "animate-pulse" : ""}`}
				style={{ color }}
			>
				{pending || syncing ? (
					<>
						<span className="leading-none text-3xl">↻</span>
						<span className="leading-none text-xl">⌛</span>
					</>
				) : (
					<span className="leading-none text-3xl">✓</span>
				)}
			</button>
		);
	};

	return (
		<div style={{ height: "calc(100dvh - 3.5rem - env(safe-area-inset-top))", overflowY: "auto", paddingBottom: "env(safe-area-inset-bottom)" }}>
		<div className="max-w-md mx-auto w-full px-2 sm:px-0">
			<div className="text-center mt-6 mb-4">
				<div className="text-5xl font-bold text-blu-uss tracking-tight">
					{orario.toLocaleTimeString("it-IT")}
				</div>
				<div className="mt-1 text-base text-gray-500 uppercase tracking-wider">
					{orario.toLocaleDateString("it-IT", { weekday: "long", year: "numeric", month: "long", day: "numeric" })}
				</div>
			</div>
			<div className="space-y-6">
			<div className="p-5 bg-white rounded-2xl shadow-lg border-2 border-blu-uss">
			{/* Modal QR Reader */}
			{qrMode && (
				<div className="fixed inset-0 bg-black bg-opacity-60 flex flex-col items-center justify-center z-50">
					<div className="bg-white rounded-lg p-4 shadow-lg flex flex-col items-center">
						<div className="font-bold mb-2">Scansiona il QR code della location</div>
						<QrReader onScan={onQrScan} onError={onQrError} onClose={handleScannerClosed} />
						<button className="mt-4 px-4 py-2 bg-gray-300 text-gray-800 rounded" disabled={closingScanner} onClick={handleManualClose}>
							Chiudi
						</button>
					</div>
				</div>
			)}
			{/* Pulsanti timbratura */}
			<div className="grid grid-cols-2 gap-3">
				{TIMBRATURE_LABELS.map(btn => {
					const enabled = isButtonEnabled(btn.tipo);
					const palette = BUTTON_THEMES[btn.theme] || BUTTON_THEMES.green;
					const colorClass = enabled ? palette.active : `${palette.disabled} cursor-not-allowed`;
					return (
						<button
							key={btn.tipo}
							className={`flex items-center justify-center h-16 sm:h-20 rounded-2xl text-lg font-bold shadow transition-colors duration-200 border ${colorClass} ${btn.layout || ""}`}
							onClick={() => enabled && handleTimbratura(btn.tipo)}
							disabled={!enabled}
						>
							<span className="mr-3 text-2xl leading-none">{btn.icon}</span> {btn.label}
						</button>
					);
				})}
			</div>
			<div className="mt-4 min-h-[40px] flex flex-col items-center justify-center text-sm font-semibold">
				{gpsLoading && !qrMode && <div className="text-blue-700 text-center">Rilevazione posizione GPS in corso...</div>}
				{qrInfo && !qrMode && <div className="text-emerald-700 text-center">{qrInfo}</div>}
				{qrError && !qrMode && <div className="text-red-600 text-center">{qrError}</div>}
				{!gpsLoading && !qrInfo && !qrError && <div className="invisible">placeholder</div>}
			</div>
			{/* Toggle timbrata fuori orario */}
			<div className="mt-2 flex items-center justify-between">
				<span className="text-base font-semibold text-blu-uss">Timbrata fuori orario</span>
				<button
					type="button"
					className={`relative w-16 h-8 rounded-full border transition-colors duration-200 ${fuoriOrarioEnabled ? "bg-green-500 border-green-600" : "bg-gray-200 border-gray-300"}`}
					onClick={() => setFuoriOrarioEnabled(prev => !prev)}
					aria-pressed={fuoriOrarioEnabled}
					title="Attiva se stai registrando una timbrata fuori orario"
				>
					<span className="sr-only">Attiva/disattiva timbrata fuori orario</span>
					<span
						className={`absolute inset-y-1 left-1 h-6 w-6 rounded-full bg-white shadow transform transition-transform duration-200 ${fuoriOrarioEnabled ? "translate-x-8" : "translate-x-0"}`}
					/>
				</button>
			</div>
			</div>
			<div className="relative p-5 rounded-2xl shadow-lg border-2 border-blu-uss" style={{ backgroundColor: "#DCEAF7" }}>
				<div className="flex flex-wrap items-center justify-between gap-2">
					<span className="text-base font-semibold text-blue-900">{dataRifLabel}</span>
					<SyncIconButton pending={nonSynced} syncing={syncing} onClick={async () => {
						await resetRetryCount(user?.id);
						await performSync();
					}} />
				</div>
				<ul className="bg-white rounded-2xl border border-gray-200 p-3 text-sm min-h-[48px] space-y-1">
					{loadingGiornata ? (
						<li className="text-gray-400">Caricamento timbrature...</li>
					) : giornata.length === 0 ? (
						<li className="text-gray-400">Nessuna timbratura per questa giornata.</li>
					) : (
						giornata.map(t => (
							<li key={t.id} className="flex items-center gap-3 py-1 text-sm">
								<span className={`flex-none w-16 font-semibold ${t.fuori_orario ? "text-red-600" : "text-gray-800"}`}>
									{t.fuori_orario && t.orario_teorico ? t.orario_teorico : formatHHMM(t.orario)}
								</span>
								<span className={`flex-1 font-semibold truncate ${t.fuori_orario ? "text-red-600" : "text-gray-900"}`}>
									{TIMBRATURE_LABELS.find(b => b.tipo === t.tipo)?.label || t.tipo}
								</span>
								<SyncIcon data_sync={t.data_sync} onRetry={async () => {
									await resetSingleRetry(t.id);
									await performSync({ focusDate: t.data_riferimento });
								}} />
							</li>
						))
					)}
				</ul>
				{lastSyncAt && (
					<div className="mt-3 text-xs text-gray-500 font-medium text-right">
						Aggiornato: {lastSyncAt.toLocaleTimeString("it-IT", { hour: "2-digit", minute: "2-digit" })}
					</div>
				)}
			</div>
			{showFuoriOrarioModal && (
				<div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-50">
					<div className="bg-white rounded-lg p-5 shadow-lg w-full max-w-sm">
						<h3 className="text-lg font-bold text-gray-800">Inserisci l'orario corretto</h3>
						<p className="text-sm text-gray-600 mt-1">Questa informazione verrà inviata all'HR insieme alla timbrata.</p>
						{isWindowsPlatform ? (
							<div className="mt-4 grid grid-cols-2 gap-3">
								<div>
									<label className="text-xs font-semibold text-gray-600">Ora</label>
									<select
										className="mt-1 w-full border-2 border-gray-300 rounded-xl px-3 py-2 text-lg font-semibold text-center text-gray-800 bg-white focus:border-green-600 focus:ring-2 focus:ring-green-300"
										value={fuoriOrarioTime.split(":")[0] || "00"}
										onChange={e => {
											const minutes = fuoriOrarioTime.split(":")[1] || "00";
											setFuoriOrarioTime(`${e.target.value}:${minutes}`);
										}}
									>
										{HOURS_24.map(h => (
											<option key={h} value={h}>{h}</option>
										))}
									</select>
								</div>
								<div>
									<label className="text-xs font-semibold text-gray-600">Minuti</label>
									<select
										className="mt-1 w-full border-2 border-gray-300 rounded-xl px-3 py-2 text-lg font-semibold text-center text-gray-800 bg-white focus:border-green-600 focus:ring-2 focus:ring-green-300"
										value={fuoriOrarioTime.split(":")[1] || "00"}
										onChange={e => {
											const hours = fuoriOrarioTime.split(":")[0] || "00";
											setFuoriOrarioTime(`${hours}:${e.target.value}`);
										}}
									>
										{MINUTES_60.map(m => (
											<option key={m} value={m}>{m}</option>
										))}
									</select>
								</div>
							</div>
						) : (
							<>
								<input
									type="time"
									lang="it-IT"
									step="60"
									inputMode="numeric"
									pattern="[0-9]{2}:[0-9]{2}"
									placeholder="hh:mm"
									className="mt-4 w-full border-2 border-gray-300 rounded-xl px-4 py-3 text-lg font-semibold text-center tracking-widest text-gray-800 bg-white focus:border-green-600 focus:ring-2 focus:ring-green-300 transition-shadow appearance-none"
									value={fuoriOrarioTime}
									onFocus={e => e.target.showPicker?.()}
									onChange={e => setFuoriOrarioTime(e.target.value)}
								/>
							</>
						)}
						<p className="text-xs text-gray-500 mt-1">Formato 24h (es. 14:30)</p>
						<div className="mt-5 flex flex-wrap justify-end gap-3">
							<button
								type="button"
								className="px-4 py-2 rounded border border-gray-300 text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-gray-300 focus:ring-offset-1"
								onClick={handleFuoriOrarioCancel}
							>
								Annulla
							</button>
							<button
								type="button"
								className="px-4 py-2 rounded border border-green-600 bg-green-600 text-white font-semibold shadow hover:bg-green-700 hover:border-green-700 focus:outline-none focus:ring-2 focus:ring-green-400 focus:ring-offset-1"
								onClick={handleFuoriOrarioConfirm}
							>
								Conferma
							</button>
						</div>
					</div>
				</div>
			)}
			</div>
		</div>
		</div>
	);
}

export default Timbratore;
